import React, { useEffect, useState } from 'react'
import { superAxiosRequest } from 'axiosApi'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import styles from './AnsweringTemplates.module.scss'
import { HeaderAddButton } from 'components/UI/SuperButton/SuperButton'
import asideModalStore from 'Stores/asideModalStore'
import swal from 'sweetalert'
import ActionMenu from 'components/UI/ActionMenu/ActionMenu'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import AddNewTemplates from './AddNewTemplates/AddNewTemplates'
import { checkPermList } from 'helpers'
import { permissions } from 'settings/permissions'
import Pagination from 'components/UI/Pagination/Pagination'

const initPagination = {
  page: 1,
  limit: 10,
}

export const AnsweringTemplates = () => {
  const [templates, setTemplates] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState(initPagination)

  const fetchTemplates = async () => {
    setIsLoading(true)

    try {
      const response = await superAxiosRequest({
        link: 'integration/templates_wazzup',
        method: 'get',
        crm: true,
        params: {
          offset: (pagination.page - 1) * pagination.limit,
          limit: pagination.limit,
        },
      })
      setTemplates(response.data)
    } catch (err) {
      console.error('Ошибка загрузки шаблонов:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchTemplates()
  }, [pagination])

  const addTemplate = () => {
    const modalConfig = {
      modalBodyComponent: () => <AddNewTemplates />,
      link: 'integration/templates_wazzup',
      isResponsive: true,
      crm: true,
      title: 'Добавить шаблон',
      button: 'Сохранить',
      valid: {
        title: 'required',
        text: 'required',
      },
      onSubmitSuccess: () => fetchTemplates(),
    }
    asideModalStore.activity(modalConfig, { is_active: true, channel_id: null })
  }

  const editTemplate = (template) => {
    const modalConfig = {
      modalBodyComponent: () => <AddNewTemplates />,
      isResponsive: true,
      crm: true,
      title: 'Редактировать шаблон',
      button: 'Сохранить',
      is_put: true,
      link: `integration/templates_wazzup/${template.id}`,
      valid: {
        title: 'required',
        text: 'required',
      },
      onSubmitSuccess: () => fetchTemplates(),
    }

    let fileType = null
    if (template.file_type) {
      fileType = template.file_type
    } else if (template.file) {
      const extension = template.file.split('.').pop().toLowerCase()
      if (['jpeg', 'jpg', 'png', 'tif'].includes(extension)) {
        fileType = 'image'
      } else if (['mp3'].includes(extension)) {
        fileType = 'audio'
      } else if (['mp4'].includes(extension)) {
        fileType = 'video'
      } else if (
        [
          'pdf',
          'xls',
          'xlsx',
          'doc',
          'docx',
          'pptx',
          'ppt',
          'txt',
          'zip',
          'rar',
          'jpeg',
          'jpg',
          'png',
          'tif',
        ].includes(extension)
      ) {
        fileType = 'document'
      } else {
        fileType = 'document'
      }
    }

    asideModalStore.activity(modalConfig, {
      channel_id: template.channel?.id || null,
      is_active: template.is_active,
      text: template.text,
      title: template.title,
      file: template.file || null,
      file_type: fileType,
    })
  }

  const deleteTemplate = (templateId) => {
    swal({
      title: 'Вы уверены?',
      text: 'Шаблон будет удален безвозвратно.',
      icon: 'warning',
      buttons: ['Отмена', 'Удалить'],
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        superAxiosRequest({
          method: 'delete',
          link: `integration/templates_wazzup/${templateId}`,
          crm: true,
        })
          .then(() => {
            fetchTemplates()
          })
          .catch((err) => {
            swal('Ошибка', 'Не удалось удалить шаблон', 'error')
            console.error('Ошибка удаления:', err)
          })
      }
    })
  }

  const handleMenuAction = (actionType, template) => {
    const actions = {
      edit_template: () => editTemplate(template),
      delete_template: () => deleteTemplate(template.id),
    }
    actions[actionType]?.()
  }

  const menuItems = [
    { name: 'Редактировать', action: 'edit_template' },
    { name: 'Удалить', action: 'delete_template' },
  ]

  const renderTemplateRow = (template, index) => (
    <div
      key={template.id}
      className={`${styles.templateRow} ${index % 2 === 0 ? styles.colorBg : ''}`}
    >
      <div className={styles.colTitle}>{template.title}</div>
      <div className={styles.colText}>{template.text}</div>
      <div className={styles.colChannel}>{template.channel?.name || '—'}</div>
      <div className={styles.colFile}>{template.file ? 'Да' : 'Нет'}</div>
      <div className={styles.colActive}>
        {template.is_active ? 'Да' : 'Нет'}
      </div>
      <div className={styles.colActions}>
        {checkPermList(permissions.edit_response_templates_wazzup) && (
          <ActionMenu
            menu={menuItems}
            action={(actionType) => handleMenuAction(actionType, template)}
          />
        )}
      </div>
    </div>
  )

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <PageHeader title='Шаблоны ответов' />
        {checkPermList(permissions.edit_response_templates_wazzup) && (
          <HeaderAddButton title='Добавить шаблон' onClick={addTemplate} />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : templates?.result?.length === 0 ? (
        <div className={styles.noTemplates}>Нет добавленных шаблонов</div>
      ) : (
        <div className={styles.templatesList}>
          <div className={`${styles.templateRow} ${styles.headerRow}`}>
            <div className={styles.colTitle}>Имя шаблона</div>
            <div className={styles.colText}>Текст шаблона</div>
            <div className={styles.colChannel}>Канал</div>
            <div className={styles.colFile}>Файл</div>
            <div className={styles.colActive}>Активный</div>
            <div className={styles.colActions}></div>
          </div>
          {templates?.result?.map((template, index) =>
            renderTemplateRow(template, index),
          )}
        </div>
      )}
      <div className={styles.pagination}>
        {templates?._meta?.count_items > 5 && (
          <Pagination
            limit={pagination.limit}
            count={templates?._meta?.count_items || 0}
            page={pagination.page}
            onChange={(page) => setPagination({ ...pagination, page })}
            onChangePerPage={(limit) => {
              setPagination({ page: 1, limit })
            }}
          />
        )}
      </div>
    </div>
  )
}
